import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Draws a two-column "label | value" sheet (the COSECHA / TURNO layout) to Excel or PDF.
//
// sheet = {
//   name, fileBase, header, headerColor ('RRGGBB'), borderColor, boldLabels,
//   rows: [{ label, text, date?: 'YYYY-MM-DD', number?, decimal?, fill?: 'RRGGBB', labelFill?: 'RRGGBB' }
//          | { section: true, label, fill? } — a full-width bold title row (e.g. OBSERVACIONES)],
//   banner?: { text, fill } — one full-width bold row under the table (e.g. Observación)
// }

const DEFAULT_BORDER = 'A9C29F';

function hexToRgb(hex) {
  return [0, 2, 4].map((i) => parseInt(hex.substring(i, i + 2), 16));
}

function parseISODate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
}

async function download(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function exportSheetToExcel(sheet) {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(sheet.name, { views: [{ showGridLines: false }] });
  ws.columns = [{ width: 32 }, { width: 32 }];

  const border = { style: 'thin', color: { argb: `FF${sheet.borderColor || DEFAULT_BORDER}` } };
  const allBorders = { top: border, left: border, bottom: border, right: border };
  const baseFont = { name: 'Calibri', size: 12 };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

  const addFullWidthRow = (text, fill, size, height) => {
    const row = ws.addRow([text, '']);
    ws.mergeCells(row.number, 1, row.number, 2);
    row.height = height;
    const cell = row.getCell(1);
    cell.font = { ...baseFont, size, bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = solid(fill);
    row.getCell(1).border = allBorders;
    row.getCell(2).border = allBorders;
  };

  addFullWidthRow(sheet.header, sheet.headerColor, 14, 22);

  sheet.rows.forEach((r) => {
    if (r.section) {
      addFullWidthRow(r.label, r.fill || 'FFFFFF', 12, 19);
      return;
    }
    let value = r.text;
    if (r.date) {
      const { y, m, d } = parseISODate(r.date);
      value = new Date(Date.UTC(y, m - 1, d));
    } else if (typeof r.number === 'number' && !Number.isNaN(r.number)) {
      value = r.number;
    }
    const row = ws.addRow([r.label, value]);
    row.height = 19;
    const labelCell = row.getCell(1);
    const valueCell = row.getCell(2);
    labelCell.font = { ...baseFont, bold: !!sheet.boldLabels };
    valueCell.font = baseFont;
    labelCell.alignment = { horizontal: 'left', vertical: 'middle' };
    valueCell.alignment = { horizontal: 'center', vertical: 'middle' };
    labelCell.border = allBorders;
    valueCell.border = allBorders;
    if (r.date) valueCell.numFmt = 'd/m/yyyy';
    if (r.decimal) valueCell.numFmt = '0.0#';
    if (r.fill) valueCell.fill = solid(r.fill);
    if (r.labelFill) labelCell.fill = solid(r.labelFill);
  });

  if (sheet.banner) addFullWidthRow(sheet.banner.text, sheet.banner.fill, 12, 19);

  await download(workbook, `${sheet.fileBase}.xlsx`);
}

export function exportSheetToPDF(sheet) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const tableWidth = 150;
  const marginX = (doc.internal.pageSize.getWidth() - tableWidth) / 2;
  const body = sheet.rows.map((r) => (r.section
    ? [{
      content: r.label,
      colSpan: 2,
      styles: { halign: 'center', fontStyle: 'bold', fillColor: hexToRgb(r.fill || 'FFFFFF') },
    }]
    : [r.label, r.text]));
  if (sheet.banner) {
    body.push([{
      content: sheet.banner.text,
      colSpan: 2,
      styles: { halign: 'center', fontStyle: 'bold', fillColor: hexToRgb(sheet.banner.fill) },
    }]);
  }

  autoTable(doc, {
    startY: 20,
    margin: { left: marginX, right: marginX },
    tableWidth,
    theme: 'grid',
    head: [[{ content: sheet.header, colSpan: 2 }]],
    body,
    styles: {
      font: 'helvetica',
      fontSize: 11,
      textColor: [0, 0, 0],
      fillColor: [255, 255, 255],
      lineColor: hexToRgb(sheet.borderColor || DEFAULT_BORDER),
      lineWidth: 0.25,
      cellPadding: { top: 1.8, bottom: 1.8, left: 2, right: 2 },
    },
    headStyles: {
      fillColor: hexToRgb(sheet.headerColor),
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 13,
      halign: 'center',
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: tableWidth / 2, fontStyle: sheet.boldLabels ? 'bold' : 'normal' },
      1: { halign: 'center', cellWidth: tableWidth / 2 },
    },
    didParseCell: (data) => {
      const r = sheet.rows[data.row.index];
      if (data.section !== 'body' || !r || r.section) return;
      if (data.column.index === 1 && r.fill) data.cell.styles.fillColor = hexToRgb(r.fill);
      if (data.column.index === 0 && r.labelFill) data.cell.styles.fillColor = hexToRgb(r.labelFill);
    },
  });

  doc.save(`${sheet.fileBase}.pdf`);
}

export function safeFilePart(text, fallback) {
  return (text || fallback).replace(/[^\w-]+/g, '_');
}
